import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — ExpandableCard. Dep-free card-shaped disclosure
 * (no @radix-ui/react-accordion). A surface card whose header toggles a detail
 * region that collapses via a max-height/opacity CSS transition. Single 1px
 * outline only — no colored borders or accent bars.
 */
export interface ExpandableCardProps
  extends React.HTMLAttributes<HTMLDivElement> {
  header: React.ReactNode;
  defaultOpen?: boolean;
}

export const ExpandableCard = React.forwardRef<HTMLDivElement, ExpandableCardProps>(
  ({ className, header, defaultOpen = false, children, ...props }, ref) => {
    const [open, setOpen] = React.useState(defaultOpen);
    const contentId = React.useId();

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-so-lg border border-sober-outline bg-sober-surface',
          className
        )}
        {...props}
      >
        <button
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            'flex w-full cursor-pointer items-center justify-between p-4 px-5 text-left',
            'text-so-14 font-bold text-sober-text',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary-fg'
          )}
        >
          <span className="min-w-0">{header}</span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'size-4 shrink-0 text-sober-secondary transition-transform duration-200',
              open && 'rotate-180'
            )}
          />
        </button>
        <div
          id={contentId}
          role="region"
          className={cn(
            'overflow-hidden text-so-14 text-sober-secondary transition-all duration-200',
            open
              ? 'max-h-[400px] border-t border-sober-outline px-5 pb-5 pt-4 opacity-100'
              : 'max-h-0 opacity-0'
          )}
        >
          {children}
        </div>
      </div>
    );
  }
);
ExpandableCard.displayName = 'ExpandableCard';
